import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { hrms, interviews as interviewsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import StatCard from '../../components/common/StatCard';
import { theme } from '../../theme';

// ─── Constants ───────────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'candidates', label: 'Candidates' },
  { id: 'panels', label: 'Panels' },
  { id: 'schedule', label: 'My Schedule' },
  { id: 'scorecards', label: 'Scorecards' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'offers', label: 'Offers' },
  { id: 'templates', label: 'Templates' },
  { id: 'questions', label: 'Question Bank' },
  { id: 'grades', label: 'Salary Grades' },
];

const CANDIDATE_STATUSES = ['new', 'screening', 'interview', 'offer', 'hired', 'rejected'];
const STATUS_COLORS = {
  new: theme.textMuted, screening: theme.info, interview: theme.warning,
  offer: '#8B5CF6', hired: theme.success, rejected: theme.danger,
};
const ROUND_TYPES = ['technical', 'behavioral', 'hr_fit', 'culture_fit', 'final', 'case_study'];
const INTERVIEW_TYPES = ['technical', 'behavioral', 'hr_fit', 'culture_fit', 'final', 'case_study'];
const DIFFICULTY_OPTS = ['easy', 'medium', 'hard'];
const RECOMMENDATION_OPTS = ['select', 'reject', 'hold'];
const OUTCOME_OPTS = ['select', 'reject', 'hold', 'waitlist'];
const OFFER_STATUS_COLORS = {
  draft: theme.textMuted, pending_approval: theme.warning, approved: theme.info,
  sent: '#8B5CF6', accepted: theme.success, declined: theme.danger,
  expired: theme.textMuted, revoked: theme.danger,
};
const SOURCES = ['LinkedIn', 'Naukri', 'Indeed', 'Referral', 'Walk-in', 'Campus', 'Agency', 'Other'];

const EMPTY_CANDIDATE = {
  full_name: '', email: '', phone: '', position_applied: '', source: '',
  experience_years: '', current_company: '', current_role: '',
  current_ctc: '', expected_ctc: '', notice_period_days: '',
  skills: '', notes: '', linkedin_url: '', status: 'new', requisition_id: '',
};
const EMPTY_ROUND = {
  round_number: 1, round_type: 'technical', interviewer_name: '',
  panel_id: '', scheduled_at: '', status: 'scheduled', feedback: '', rating: '',
};
const EMPTY_PANEL = {
  name: '', position: '', department_id: '', panel_lead_user_id: '',
  members: [],
};
const EMPTY_TEMPLATE = { name: '', description: '', interview_type: '', criteria: [] };
const EMPTY_QUESTION = { question_text: '', interview_type: '', difficulty: 'medium', tags: '' };
const EMPTY_GRADE = { grade_code: '', grade_name: '', min_ctc: '', max_ctc: '', override_threshold_pct: 10, department_id: '' };
const EMPTY_OFFER_NEW = {
  candidate_id: '', designation: '', department_id: '', grade_id: '',
  offered_ctc: '', joining_date: '', offer_valid_until: '', offer_notes: '',
};
const EMPTY_DECISION = {
  candidate_id: '', outcome: 'select', recommendation_notes: '', final_score: '',
  approver_ids: [],
};

function fmt(val) {
  if (!val) return '—';
  return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}
function fmtDateOnly(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

// ─── Style helpers ────────────────────────────────────────────────────────────
const s = {
  tabs: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20, borderBottom: `2px solid ${theme.border}` },
  tab: (active) => ({
    padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 500,
    borderBottom: active ? `2px solid ${theme.primary}` : '2px solid transparent',
    color: active ? theme.primary : theme.textMuted, background: 'none', border: 'none',
    marginBottom: -2,
  }),
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, color: theme.textMuted, fontWeight: 500 },
  input: {
    padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.border}`,
    background: theme.cardBg, color: theme.text, fontSize: 14, width: '100%',
  },
  select: {
    padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.border}`,
    background: theme.cardBg, color: theme.text, fontSize: 14, width: '100%',
  },
  textarea: {
    padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.border}`,
    background: theme.cardBg, color: theme.text, fontSize: 14, width: '100%',
    minHeight: 80, resize: 'vertical',
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: theme.text, marginBottom: 12 },
  infoRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  pill: (color) => ({
    background: color + '22', color, border: `1px solid ${color}44`,
    borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600,
  }),
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' },
  divider: { border: 'none', borderTop: `1px solid ${theme.border}`, margin: '12px 0' },
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function Interviews() {
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || 'dashboard');
  const isHR = user?.role === 'hr_admin' || user?.role === 'super_admin';
  const isManager = isHR || user?.role === 'reporting_manager';

  // Filter tabs by role
  const visibleTabs = TABS.filter(t => {
    if (['grades'].includes(t.id) && !isHR) return false;
    if (['feedback', 'templates', 'questions', 'panels', 'decisions'].includes(t.id) && !isManager) return false;
    return true;
  });

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: theme.text, margin: 0 }}>Interview Management</h2>
        <p style={{ color: theme.textMuted, marginTop: 4, fontSize: 14 }}>
          End-to-end recruitment interview lifecycle management
        </p>
      </div>

      <div style={s.tabs}>
        {visibleTabs.map(t => (
          <button key={t.id} style={s.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'candidates' && <CandidatesTab isHR={isHR} />}
      {tab === 'panels' && isManager && <PanelsTab isHR={isHR} />}
      {tab === 'schedule' && <ScheduleTab />}
      {tab === 'scorecards' && <ScorecardsTab isHR={isHR} />}
      {tab === 'feedback' && isManager && <FeedbackTab isHR={isHR} />}
      {tab === 'decisions' && isManager && <DecisionsTab isHR={isHR} />}
      {tab === 'offers' && isManager && <OffersTab isHR={isHR} />}
      {tab === 'templates' && isManager && <TemplatesTab isHR={isHR} />}
      {tab === 'questions' && isManager && <QuestionsTab isHR={isHR} />}
      {tab === 'grades' && isHR && <SalaryGradesTab isSuperAdmin={user?.role === 'super_admin'} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════════════════════
function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      interviewsApi.getDashboardStats(),
      interviewsApi.getPipeline(),
    ]).then(([s, p]) => {
      setStats(s.data);
      setPipeline(p.data);
    }).catch(err => {
      setError(err.response?.data?.detail || 'Failed to load dashboard. Check backend connection.');
    }).finally(() => setLoading(false));
  }, []);

  if (error) return <div style={{ color: theme.danger, padding: 16, background: theme.danger + '11', borderRadius: 8 }}>{error}</div>;

  if (loading) return <div style={{ color: theme.textMuted }}>Loading dashboard…</div>;

  const pipelineData = pipeline ? Object.entries(pipeline).map(([k, v]) => ({
    name: fmt(k), value: v, fill: STATUS_COLORS[k] || theme.primary,
  })) : [];

  return (
    <div>
      <div style={s.grid4}>
        <StatCard label="Active Candidates" value={stats?.active_candidates ?? 0} color={theme.primary} />
        <StatCard label="Rounds This Week" value={stats?.rounds_this_week ?? 0} color={theme.info} />
        <StatCard label="Pending Scorecards" value={stats?.pending_scorecards ?? 0} color={theme.warning} />
        <StatCard label="Offers Sent" value={stats?.offers_sent ?? 0} color="#8B5CF6" />
      </div>
      <div style={{ ...s.grid2, marginTop: 16 }}>
        <StatCard label="Pending Approvals" value={stats?.pending_approvals ?? 0} color={theme.danger} />
        <StatCard label="Offer Acceptance Rate" value={`${stats?.offer_acceptance_rate ?? 0}%`} color={theme.success} />
      </div>

      <div style={{ marginTop: 24 }}>
        <Card>
          <div style={s.sectionTitle}>Candidate Pipeline</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pipelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="name" tick={{ fill: theme.textMuted, fontSize: 12 }} />
              <YAxis tick={{ fill: theme.textMuted, fontSize: 12 }} />
              <Tooltip contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}` }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {pipelineData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANDIDATES TAB
// ═══════════════════════════════════════════════════════════════════════════════
function CandidatesTab({ isHR }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(null); // 'add'|'edit'|'view'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_CANDIDATE);
  const [rounds, setRounds] = useState([]);
  const [roundModal, setRoundModal] = useState(false);
  const [roundForm, setRoundForm] = useState(EMPTY_ROUND);
  const [users, setUsers] = useState([]);
  const [eventLog, setEventLog] = useState([]);
  const [logModal, setLogModal] = useState(false);
  const [panels, setPanels] = useState([]);
  const [onboarding, setOnboarding] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [docType, setDocType] = useState('resume');

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (search) params.search = search;
    const res = await hrms.getCandidates(params).catch(() => ({ data: [] }));
    setCandidates(res.data);
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => {
    load();
    if (isHR) {
      interviewsApi.getPanels({ is_active: true }).then(r => setPanels(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    }
  }, [load, isHR]);

  const loadUsers = () => {
    if (users.length === 0) {
      interviewsApi.getEmployeesForPanel().then(r => setUsers(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    }
  };

  const handleSave = async () => {
    const data = { ...form };
    if (data.experience_years === '') data.experience_years = null;
    if (data.notice_period_days === '') data.notice_period_days = null;
    if (data.current_ctc === '') data.current_ctc = null;
    if (data.expected_ctc === '') data.expected_ctc = null;
    try {
      if (selected) {
        await hrms.updateCandidate(selected.id, data);
      } else {
        await hrms.createCandidate(data);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving candidate');
    }
  };

  const handleAddRound = async () => {
    const data = { ...roundForm };
    if (data.rating === '') data.rating = null;
    if (!data.scheduled_at) data.scheduled_at = null;
    if (!data.panel_id) data.panel_id = null;
    else data.panel_id = parseInt(data.panel_id);
    try {
      await hrms.addInterviewRound(selected.id, data);
      const r = await hrms.getInterviewRounds(selected.id);
      setRounds(r.data);
      setRoundModal(false);
      setRoundForm(EMPTY_ROUND);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error adding round');
    }
  };

  const openView = async (c) => {
    setSelected(c);
    const r = await hrms.getInterviewRounds(c.id).catch(() => ({ data: [] }));
    setRounds(r.data);
    setModal('view');
  };

  const openLog = async (c) => {
    const r = await interviewsApi.getCandidateEventLog(c.id).catch(() => ({ data: [] }));
    setEventLog(r.data);
    setLogModal(true);
  };

  const handleAssignPanel = async (candidateId, panelId) => {
    await interviewsApi.assignPanel(candidateId, panelId).catch(err => console.warn('[HRMS]', err?.message));
    alert('Panel assigned');
  };

  const handleOnboard = async () => {
    if (!selected) return;
    if (!window.confirm(`Generate CAND code and send login credentials to ${selected.email}?`)) return;
    setOnboarding(true);
    try {
      const r = await hrms.onboardCandidate(selected.id);
      alert(`Onboarded! Login: ${r.data.cand_code} — credentials sent to ${selected.email}`);
      const updated = await hrms.getCandidate(selected.id);
      setSelected(updated.data);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Onboarding failed');
    } finally { setOnboarding(false); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('doc_type', docType);
      fd.append('file', file);
      await hrms.uploadCandidateDoc(selected.id, fd);
      const updated = await hrms.getCandidate(selected.id);
      setSelected(updated.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Upload failed');
    } finally { setDocUploading(false); e.target.value = ''; }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await hrms.deleteCandidateDoc(selected.id, docId).catch(() => {});
    const updated = await hrms.getCandidate(selected.id);
    setSelected(updated.data);
  };

  return (
    <div>
      <div style={s.actions}>
        <input
          placeholder="Search name, position, company…"
          style={{ ...s.input, width: 280 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select style={{ ...s.select, width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {CANDIDATE_STATUSES.map(s => <option key={s} value={s}>{fmt(s)}</option>)}
        </select>
        <Button onClick={load} variant="secondary" size="sm">Search</Button>
        {isHR && <Button onClick={() => { setForm(EMPTY_CANDIDATE); setSelected(null); setModal('add'); }} size="sm">+ Add Candidate</Button>}
      </div>

      {loading ? <div style={{ color: theme.textMuted }}>Loading…</div> : (
        <Table
          columns={[
            { header: 'Name', accessor: 'full_name' },
            { header: 'Position', accessor: 'position_applied' },
            { header: 'Experience', render: r => r.experience_years != null ? `${r.experience_years}y` : '—' },
            { header: 'Source', accessor: 'source', render: val => val || '—' },
            {
              header: 'Status', render: r => (
                <span style={s.pill(STATUS_COLORS[r.status] || theme.textMuted)}>{fmt(r.status)}</span>
              )
            },
            { header: 'Applied', render: r => fmtDateOnly(r.created_at) },
            {
              header: 'Actions', render: r => (
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button size="xs" variant="secondary" onClick={() => openView(r)}>View</Button>
                  {isHR && <Button size="xs" onClick={() => { setSelected(r); setForm({ ...r }); setModal('edit'); }}>Edit</Button>}
                  {isHR && <Button size="xs" variant="secondary" onClick={() => openLog(r)}>Log</Button>}
                </div>
              )
            },
          ]}
          data={candidates}
        />
      )}

      {/* Add / Edit Candidate Modal */}
      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => setModal(null)}
        title={modal === 'edit' ? 'Edit Candidate' : 'Add Candidate'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Full Name *</label>
              <input style={s.input} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Email</label>
              <input style={s.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Phone</label>
              <input style={s.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Position Applied</label>
              <input style={s.input} value={form.position_applied} onChange={e => setForm(f => ({ ...f, position_applied: e.target.value }))} /></div>
          </div>
          <div style={s.grid3}>
            <div style={s.field}><label style={s.label}>Experience (Years)</label>
              <input style={s.input} type="number" step="0.5" value={form.experience_years} onChange={e => setForm(f => ({ ...f, experience_years: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Current CTC (LPA)</label>
              <input style={s.input} type="number" step="0.1" value={form.current_ctc} onChange={e => setForm(f => ({ ...f, current_ctc: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Expected CTC (LPA)</label>
              <input style={s.input} type="number" step="0.1" value={form.expected_ctc} onChange={e => setForm(f => ({ ...f, expected_ctc: e.target.value }))} /></div>
          </div>
          <div style={s.grid3}>
            <div style={s.field}><label style={s.label}>Current Company</label>
              <input style={s.input} value={form.current_company} onChange={e => setForm(f => ({ ...f, current_company: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Current Role</label>
              <input style={s.input} value={form.current_role} onChange={e => setForm(f => ({ ...f, current_role: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Notice Period (Days)</label>
              <input style={s.input} type="number" value={form.notice_period_days} onChange={e => setForm(f => ({ ...f, notice_period_days: e.target.value }))} /></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Source</label>
              <select style={s.select} value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                <option value="">Select source</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Status</label>
              <select style={s.select} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {CANDIDATE_STATUSES.map(s => <option key={s} value={s}>{fmt(s)}</option>)}
              </select></div>
          </div>
          <div style={s.field}><label style={s.label}>Skills (comma-separated)</label>
            <input style={s.input} value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>LinkedIn URL</label>
            <input style={s.input} value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>Notes</label>
            <textarea style={s.textarea} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* View Candidate Modal */}
      <Modal isOpen={modal === 'view'} onClose={() => setModal(null)} title={selected?.full_name || 'Candidate'}>
        {selected && (
          <div>
            <div style={s.grid2}>
              <div><span style={s.label}>Email:</span> {selected.email || '—'}</div>
              <div><span style={s.label}>Phone:</span> {selected.phone || '—'}</div>
              <div><span style={s.label}>Position:</span> {selected.position_applied || '—'}</div>
              <div><span style={s.label}>Experience:</span> {selected.experience_years != null ? `${selected.experience_years}y` : '—'}</div>
              <div><span style={s.label}>Current Company:</span> {selected.current_company || '—'}</div>
              <div><span style={s.label}>Current Role:</span> {selected.current_role || '—'}</div>
              <div><span style={s.label}>Current CTC:</span> {selected.current_ctc ? `${selected.current_ctc} LPA` : '—'}</div>
              <div><span style={s.label}>Expected CTC:</span> {selected.expected_ctc ? `${selected.expected_ctc} LPA` : '—'}</div>
              <div><span style={s.label}>Notice Period:</span> {selected.notice_period_days ? `${selected.notice_period_days} days` : '—'}</div>
              <div><span style={s.label}>Source:</span> {selected.source || '—'}</div>
            </div>
            <div style={{ marginTop: 8 }}><span style={s.label}>Skills:</span> {selected.skills || '—'}</div>
            {selected.notes && <div style={{ marginTop: 8 }}><span style={s.label}>Notes:</span> {selected.notes}</div>}

            {/* Candidate ID + Onboard */}
            {isHR && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: '#F8FAFC', borderRadius: 8, border: `1px solid ${theme.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    {selected.cand_code
                      ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: theme.primary, fontSize: 15 }}>{selected.cand_code}</span>
                      : <span style={{ color: theme.textMuted, fontSize: 13 }}>No candidate ID yet</span>}
                    {selected.user_id && <span style={{ marginLeft: 8, background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700 }}>✓ Portal account created</span>}
                  </div>
                  {!selected.user_id && selected.email && (
                    <Button size="sm" variant="primary" onClick={handleOnboard} disabled={onboarding}>
                      {onboarding ? 'Onboarding…' : '🚀 Onboard & Send Credentials'}
                    </Button>
                  )}
                  {!selected.email && <span style={{ color: theme.danger, fontSize: 12 }}>Add email before onboarding</span>}
                </div>
              </div>
            )}

            {/* Documents */}
            <hr style={s.divider} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={s.sectionTitle}>Documents</span>
              {isHR && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select value={docType} onChange={e => setDocType(e.target.value)}
                    style={{ ...s.select, width: 130, fontSize: 12 }}>
                    {['resume','cover_letter','id_proof','certificate','offer_letter','other'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <label style={{ background: theme.primary, color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {docUploading ? 'Uploading…' : '+ Upload'}
                    <input type="file" style={{ display: 'none' }} onChange={handleDocUpload} disabled={docUploading}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                  </label>
                </div>
              )}
            </div>
            {(!selected.documents || selected.documents.length === 0) ? (
              <div style={{ color: theme.textMuted, fontSize: 13 }}>No documents uploaded yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {selected.documents.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC', borderRadius: 6, padding: '6px 10px', border: `1px solid ${theme.border}` }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{doc.file_name}</span>
                      <span style={{ marginLeft: 8, background: '#E0F2FE', color: '#0369A1', padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{doc.doc_type}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <a href={`${require('../../services/api').API_BASE}/${doc.file_path}`} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: theme.primary, textDecoration: 'none', fontWeight: 600 }}>View</a>
                      {isHR && <button onClick={() => handleDocDelete(doc.id)}
                        style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '0 4px' }}>×</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isHR && panels.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <hr style={s.divider} />
                <div style={s.label}>Assign Panel</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <select style={{ ...s.select, flex: 1 }} id="panelSelect">
                    {panels.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <Button size="sm" onClick={() => {
                    const sel = document.getElementById('panelSelect').value;
                    if (sel) handleAssignPanel(selected.id, parseInt(sel));
                  }}>Assign</Button>
                </div>
              </div>
            )}

            <hr style={s.divider} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={s.sectionTitle}>Interview Rounds</span>
              {isHR && (
                <Button size="sm" onClick={() => { setRoundForm(EMPTY_ROUND); setRoundModal(true); loadUsers(); }}>
                  + Add Round
                </Button>
              )}
            </div>
            {rounds.length === 0 ? (
              <div style={{ color: theme.textMuted, fontSize: 13 }}>No rounds scheduled yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rounds.map(r => (
                  <div key={r.id} style={{
                    background: theme.bg, border: `1px solid ${theme.border}`,
                    borderRadius: 8, padding: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>Round {r.round_number} — {fmt(r.round_type)}</strong>
                      <span style={s.pill(r.status === 'completed' ? theme.success : r.status === 'cancelled' ? theme.danger : theme.info)}>
                        {fmt(r.status)}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
                      {r.scheduled_at ? `Scheduled: ${fmtDate(r.scheduled_at)}` : 'Not scheduled'}
                      {r.panel_name && ` · Panel: ${r.panel_name}`}
                      {r.interviewer_name && ` · Interviewer: ${r.interviewer_name}`}
                      {r.rating && ` · Rating: ${r.rating}/5`}
                    </div>
                    {r.feedback && <div style={{ fontSize: 13, marginTop: 4, color: theme.text }}>{r.feedback}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Round Modal */}
      <Modal isOpen={roundModal} onClose={() => setRoundModal(false)} title="Add Interview Round">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Round Number</label>
              <input style={s.input} type="number" min={1} value={roundForm.round_number}
                onChange={e => setRoundForm(f => ({ ...f, round_number: parseInt(e.target.value) }))} /></div>
            <div style={s.field}><label style={s.label}>Round Type</label>
              <select style={s.select} value={roundForm.round_type}
                onChange={e => setRoundForm(f => ({ ...f, round_type: e.target.value }))}>
                {ROUND_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
              </select></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Interviewer Name</label>
              <input style={s.input} value={roundForm.interviewer_name}
                onChange={e => setRoundForm(f => ({ ...f, interviewer_name: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Assign Panel (optional)</label>
              <select style={s.select} value={roundForm.panel_id}
                onChange={e => setRoundForm(f => ({ ...f, panel_id: e.target.value }))}>
                <option value="">No panel</option>
                {panels.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Scheduled At</label>
              <input style={s.input} type="datetime-local" value={roundForm.scheduled_at}
                onChange={e => setRoundForm(f => ({ ...f, scheduled_at: e.target.value }))} /></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Status</label>
              <select style={s.select} value={roundForm.status}
                onChange={e => setRoundForm(f => ({ ...f, status: e.target.value }))}>
                {['scheduled', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{fmt(s)}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Rating (1–5)</label>
              <input style={s.input} type="number" min={1} max={5} step={0.5} value={roundForm.rating}
                onChange={e => setRoundForm(f => ({ ...f, rating: e.target.value }))} /></div>
          </div>
          <div style={s.field}><label style={s.label}>Feedback</label>
            <textarea style={s.textarea} value={roundForm.feedback}
              onChange={e => setRoundForm(f => ({ ...f, feedback: e.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setRoundModal(false)}>Cancel</Button>
            <Button onClick={handleAddRound}>Save Round</Button>
          </div>
        </div>
      </Modal>

      {/* Event Log Modal */}
      <Modal isOpen={logModal} onClose={() => setLogModal(false)} title="Candidate Event Log">
        {eventLog.length === 0 ? (
          <div style={{ color: theme.textMuted }}>No events recorded.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {eventLog.map(e => (
              <div key={e.id} style={{ borderLeft: `3px solid ${theme.primary}`, paddingLeft: 12, paddingBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{fmt(e.event_type)}</div>
                {e.description && <div style={{ fontSize: 12, color: theme.text }}>{e.description}</div>}
                <div style={{ fontSize: 11, color: theme.textMuted }}>{fmtDate(e.created_at)} · by {e.performer_name || '—'}</div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PANELS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function PanelsTab({ isHR }) {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_PANEL);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newMember, setNewMember] = useState({ user_id: '', interview_type: 'technical', is_mandatory: true });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await interviewsApi.getPanels({}).catch(() => ({ data: [] }));
    setPanels(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    hrms.getDepartments().then(r => setDepartments(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    interviewsApi.getEmployeesForPanel().then(r => setUsers(r.data)).catch(err => console.warn('[HRMS]', err?.message));
  }, [load]);

  const handleSave = async () => {
    const data = {
      name: form.name,
      position: form.position,
      department_id: form.department_id || null,
      panel_lead_user_id: parseInt(form.panel_lead_user_id),
      members: (form.members || []).map(m => ({
        user_id: parseInt(m.user_id),
        interview_type: m.interview_type,
        is_mandatory: m.is_mandatory,
      })),
    };
    try {
      if (selected) {
        await interviewsApi.updatePanel(selected.id, data);
      } else {
        await interviewsApi.createPanel(data);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving panel');
    }
  };

  const handleAddMember = () => {
    if (!newMember.user_id) return;
    setForm(f => ({ ...f, members: [...(f.members || []), { ...newMember }] }));
    setNewMember({ user_id: '', interview_type: 'technical', is_mandatory: true });
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this panel?')) return;
    await interviewsApi.deletePanel(id);
    load();
  };

  const handleClone = async (id) => {
    await interviewsApi.clonePanel(id);
    load();
  };

  return (
    <div>
      <div style={s.actions}>
        {isHR && (
          <Button onClick={() => { setForm({ ...EMPTY_PANEL, members: [] }); setSelected(null); setModal('form'); }}>
            + Create Panel
          </Button>
        )}
      </div>

      {loading ? <div style={{ color: theme.textMuted }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {panels.length === 0 && <div style={{ color: theme.textMuted }}>No panels created yet.</div>}
          {panels.map(p => (
            <Card key={p.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                  <div style={{ color: theme.textMuted, fontSize: 13 }}>
                    Position: {p.position} · Dept: {p.department_name || 'All'} · Lead: {p.panel_lead_name}
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(p.members || []).map(m => (
                      <span key={m.id} style={s.pill(theme.info)}>
                        {m.user_name} ({fmt(m.interview_type)})
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {isHR && <Button size="xs" variant="secondary" onClick={() => { setSelected(p); setForm({ ...p }); setModal('form'); }}>Edit</Button>}
                  {isHR && <Button size="xs" variant="secondary" onClick={() => handleClone(p.id)}>Clone</Button>}
                  {isHR && p.is_active && <Button size="xs" variant="danger" onClick={() => handleDeactivate(p.id)}>Deactivate</Button>}
                  {!p.is_active && <span style={s.pill(theme.danger)}>Inactive</span>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={modal === 'form'} onClose={() => setModal(null)} title={selected ? 'Edit Panel' : 'Create Panel'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Panel Name *</label>
              <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Target Position *</label>
              <input style={s.input} value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} /></div>
          </div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Department</label>
              <SearchableSelect
                options={[{ value: '', label: 'All Departments' }, ...departments.map(d => ({ value: d.id, label: d.name }))]}
                value={form.department_id}
                onChange={v => setForm(f => ({ ...f, department_id: v }))}
                placeholder="All Departments"
              /></div>
            <div style={s.field}><label style={s.label}>Panel Lead *</label>
              <SearchableSelect
                options={users.map(u => ({ value: u.id, label: u.full_name }))}
                value={form.panel_lead_user_id}
                onChange={v => setForm(f => ({ ...f, panel_lead_user_id: v }))}
                placeholder="Select lead"
              /></div>
          </div>

          <hr style={s.divider} />
          <div style={s.label}>Panel Members (max 6)</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={s.field}>
              <label style={s.label}>Member</label>
              <SearchableSelect
                options={users.map(u => ({ value: u.id, label: u.full_name }))}
                value={newMember.user_id}
                onChange={v => setNewMember(m => ({ ...m, user_id: v }))}
                placeholder="Select member"
                style={{ width: 200 }}
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Interview Type</label>
              <select style={{ ...s.select, width: 140 }} value={newMember.interview_type}
                onChange={e => setNewMember(m => ({ ...m, interview_type: e.target.value }))}>
                {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
              </select>
            </div>
            <Button size="sm" onClick={handleAddMember} disabled={(form.members || []).length >= 6}>+ Add</Button>
          </div>
          {(form.members || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {form.members.map((m, i) => {
                const u = users.find(u => String(u.id) === String(m.user_id));
                return (
                  <span key={i} style={{ ...s.pill(theme.info), display: 'flex', alignItems: 'center', gap: 6 }}>
                    {u?.full_name || m.user_id} ({fmt(m.interview_type)})
                    <span
                      style={{ cursor: 'pointer', fontWeight: 700 }}
                      onClick={() => setForm(f => ({ ...f, members: f.members.filter((_, j) => j !== i) }))}
                    >×</span>
                  </span>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save Panel</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY SCHEDULE TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ScheduleTab() {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    interviewsApi.getMySchedule()
      .then(r => setSchedule(r.data))
      .catch(err => console.warn('[HRMS]', err?.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: theme.textMuted }}>Loading schedule…</div>;

  if (schedule.length === 0) {
    return <div style={{ color: theme.textMuted, padding: 24, textAlign: 'center' }}>No upcoming interviews assigned to you.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {schedule.map(r => (
        <Card key={r.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{r.candidate_name} — {r.position || '—'}</div>
              <div style={{ color: theme.textMuted, fontSize: 13 }}>
                Round {r.round_number} · {fmt(r.round_type || '')} · {fmtDate(r.scheduled_at)}
                {r.panel_name && ` · Panel: ${r.panel_name}`}
              </div>
              {r.location_or_link && (
                <div style={{ fontSize: 13, color: theme.info, marginTop: 4 }}>{r.location_or_link}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {r.scorecard_submitted ? (
                <span style={s.pill(theme.success)}>Scorecard Submitted</span>
              ) : (
                <span style={s.pill(theme.warning)}>Scorecard Pending</span>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCORECARDS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ScorecardsTab({ isHR }) {
  const { user } = useAuth();
  const [myPending, setMyPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    round_id: '', template_id: '', overall_rating: '', recommendation: '',
    strengths: '', weaknesses: '', notes: '', criterion_ratings: [],
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [existingScorecard, setExistingScorecard] = useState(null);

  useEffect(() => {
    interviewsApi.getMyPendingFeedback()
      .then(r => setMyPending(r.data))
      .catch(err => console.warn('[HRMS]', err?.message))
      .finally(() => setLoading(false));
    interviewsApi.getTemplates({ is_active: true })
      .then(r => setTemplates(r.data))
      .catch(err => console.warn('[HRMS]', err?.message));
  }, []);

  const openScorecard = async (round) => {
    setSelected(round);
    // Check if draft scorecard exists
    const roundScorecards = await interviewsApi.getRoundScorecards(round.round_id)
      .then(r => r.data).catch(() => []);
    const own = roundScorecards.find(s => s.interviewer_id === user?.id);
    if (own) {
      setExistingScorecard(own);
      setForm({
        round_id: round.round_id,
        template_id: own.template_id || '',
        overall_rating: own.overall_rating || '',
        recommendation: own.recommendation || '',
        strengths: own.strengths || '',
        weaknesses: own.weaknesses || '',
        notes: own.notes || '',
        criterion_ratings: own.criterion_ratings || [],
      });
      if (own.template_id) {
        const tmpl = templates.find(t => t.id === own.template_id);
        setSelectedTemplate(tmpl || null);
      }
    } else {
      setExistingScorecard(null);
      setForm({
        round_id: round.round_id, template_id: '', overall_rating: '',
        recommendation: '', strengths: '', weaknesses: '', notes: '', criterion_ratings: [],
      });
      setSelectedTemplate(null);
    }
    setModal('scorecard');
  };

  const handleTemplateChange = (templateId) => {
    const tmpl = templates.find(t => t.id === parseInt(templateId));
    setSelectedTemplate(tmpl || null);
    setForm(f => ({
      ...f,
      template_id: templateId,
      criterion_ratings: (tmpl?.criteria || []).map(c => ({
        criterion_id: c.id, rating: '', comment: '',
      })),
    }));
  };

  const handleSave = async (submit = false) => {
    const data = {
      ...form,
      overall_rating: form.overall_rating !== '' ? parseFloat(form.overall_rating) : null,
      template_id: form.template_id || null,
      criterion_ratings: form.criterion_ratings.filter(cr => cr.rating !== '').map(cr => ({
        ...cr, rating: parseFloat(cr.rating),
      })),
    };
    try {
      let scorecard;
      if (existingScorecard) {
        scorecard = await interviewsApi.updateScorecard(existingScorecard.id, data);
      } else {
        scorecard = await interviewsApi.createScorecard(data);
        setExistingScorecard(scorecard.data);
      }
      if (submit) {
        const scId = existingScorecard?.id || scorecard.data?.id;
        if (scId) await interviewsApi.submitScorecard(scId);
        setModal(null);
        const res = await interviewsApi.getMyPendingFeedback().catch(() => ({ data: [] }));
        setMyPending(res.data);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving scorecard');
    }
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      <div style={s.sectionTitle}>Pending Scorecards</div>
      {myPending.length === 0 ? (
        <div style={{ color: theme.textMuted }}>No pending scorecards.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {myPending.map(r => (
            <Card key={r.round_id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{r.candidate_name}</strong>
                  <span style={{ color: theme.textMuted, fontSize: 13 }}> · Round {r.round_number} · {fmtDate(r.scheduled_at)}</span>
                </div>
                <Button size="sm" onClick={() => openScorecard(r)}>Fill Scorecard</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={modal === 'scorecard'} onClose={() => setModal(null)} title={`Scorecard — Round ${selected?.round_number}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Evaluation Template (optional)</label>
            <select style={s.select} value={form.template_id} onChange={e => handleTemplateChange(e.target.value)}>
              <option value="">No template</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>

          {selectedTemplate && selectedTemplate.criteria.length > 0 && (
            <div>
              <div style={s.label}>Criterion Ratings</div>
              {selectedTemplate.criteria.map((c, i) => (
                <div key={c.id} style={{ background: theme.bg, borderRadius: 6, padding: 10, marginTop: 6 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name} {c.description && <span style={{ color: theme.textMuted, fontSize: 11 }}>— {c.description}</span>}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <input
                      type="number" min={1} max={5} step={0.5}
                      placeholder="Rating 1–5"
                      style={{ ...s.input, width: 100 }}
                      value={form.criterion_ratings[i]?.rating || ''}
                      onChange={e => {
                        const updated = [...form.criterion_ratings];
                        if (!updated[i]) updated[i] = { criterion_id: c.id, rating: '', comment: '' };
                        updated[i].rating = e.target.value;
                        setForm(f => ({ ...f, criterion_ratings: updated }));
                      }}
                    />
                    <input
                      placeholder="Comment"
                      style={{ ...s.input, flex: 1 }}
                      value={form.criterion_ratings[i]?.comment || ''}
                      onChange={e => {
                        const updated = [...form.criterion_ratings];
                        if (!updated[i]) updated[i] = { criterion_id: c.id, rating: '', comment: '' };
                        updated[i].comment = e.target.value;
                        setForm(f => ({ ...f, criterion_ratings: updated }));
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Overall Rating (1–5)</label>
              <input style={s.input} type="number" min={1} max={5} step={0.5} value={form.overall_rating}
                onChange={e => setForm(f => ({ ...f, overall_rating: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Recommendation</label>
              <select style={s.select} value={form.recommendation}
                onChange={e => setForm(f => ({ ...f, recommendation: e.target.value }))}>
                <option value="">Select</option>
                {RECOMMENDATION_OPTS.map(r => <option key={r} value={r}>{fmt(r)}</option>)}
              </select></div>
          </div>
          <div style={s.field}><label style={s.label}>Strengths</label>
            <textarea style={s.textarea} value={form.strengths}
              onChange={e => setForm(f => ({ ...f, strengths: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>Weaknesses / Areas for Improvement</label>
            <textarea style={s.textarea} value={form.weaknesses}
              onChange={e => setForm(f => ({ ...f, weaknesses: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>Additional Notes</label>
            <textarea style={s.textarea} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="secondary" onClick={() => handleSave(false)}>Save Draft</Button>
            <Button onClick={() => handleSave(true)}>Submit Scorecard</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FEEDBACK TAB
// ═══════════════════════════════════════════════════════════════════════════════
function FeedbackTab({ isHR }) {
  const [pending, setPending] = useState([]);
  const [slaConfig, setSlaConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slaModal, setSlaModal] = useState(false);
  const [slaForm, setSlaForm] = useState({ sla_hours: 24, escalation_hours: 36, offer_response_days: 7 });

  useEffect(() => {
    Promise.all([
      interviewsApi.getPendingFeedback(),
      interviewsApi.getSLAConfig(),
    ]).then(([p, cfg]) => {
      setPending(p.data);
      setSlaConfig(cfg.data);
      setSlaForm({ sla_hours: cfg.data.sla_hours, escalation_hours: cfg.data.escalation_hours, offer_response_days: cfg.data.offer_response_days });
    }).catch(err => console.warn('[HRMS]', err?.message))
    .finally(() => setLoading(false));
  }, []);

  const handleSaveSLA = async () => {
    await interviewsApi.updateSLAConfig(slaForm).catch(err => console.warn('[HRMS]', err?.message));
    setSlaModal(false);
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  const breached = pending.filter(p => p.sla_breached);
  const onTime = pending.filter(p => !p.sla_breached);

  return (
    <div>
      <div style={s.actions}>
        <div style={{ display: 'flex', gap: 16, flex: 1, flexWrap: 'wrap' }}>
          <span style={s.pill(theme.danger)}>SLA Breached: {breached.length}</span>
          <span style={s.pill(theme.warning)}>Pending: {onTime.length}</span>
          {slaConfig && (
            <span style={{ color: theme.textMuted, fontSize: 13 }}>
              SLA Window: {slaConfig.sla_hours}h · Escalation: {slaConfig.escalation_hours}h
            </span>
          )}
        </div>
        {isHR && <Button size="sm" variant="secondary" onClick={() => setSlaModal(true)}>Configure SLA</Button>}
      </div>

      {pending.length === 0 ? (
        <div style={{ color: theme.textMuted }}>No pending feedback.</div>
      ) : (
        <Table
          columns={[
            { header: 'Candidate', accessor: 'candidate_name' },
            { header: 'Interviewer', accessor: 'interviewer_name' },
            { header: 'Round', accessor: 'round_number' },
            { header: 'Scheduled', render: r => fmtDate(r.scheduled_at) },
            { header: 'Hours Elapsed', render: r => `${r.hours_elapsed}h` },
            {
              header: 'SLA Status', render: r => (
                <span style={s.pill(r.sla_breached ? theme.danger : theme.warning)}>
                  {r.sla_breached ? 'Breached' : 'Pending'}
                </span>
              )
            },
          ]}
          data={pending}
        />
      )}

      <Modal isOpen={slaModal} onClose={() => setSlaModal(false)} title="Configure SLA">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Scorecard SLA (hours)</label>
            <input style={s.input} type="number" value={slaForm.sla_hours}
              onChange={e => setSlaForm(f => ({ ...f, sla_hours: parseInt(e.target.value) }))} /></div>
          <div style={s.field}><label style={s.label}>Escalation Threshold (hours)</label>
            <input style={s.input} type="number" value={slaForm.escalation_hours}
              onChange={e => setSlaForm(f => ({ ...f, escalation_hours: parseInt(e.target.value) }))} /></div>
          <div style={s.field}><label style={s.label}>Offer Response Window (days)</label>
            <input style={s.input} type="number" value={slaForm.offer_response_days}
              onChange={e => setSlaForm(f => ({ ...f, offer_response_days: parseInt(e.target.value) }))} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setSlaModal(false)}>Cancel</Button>
            <Button onClick={handleSaveSLA}>Save</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DECISIONS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function DecisionsTab({ isHR }) {
  const [decisions, setDecisions] = useState([]);
  const [myApprovals, setMyApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_DECISION);
  const [candidates, setCandidates] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [approverIds, setApproverIds] = useState([]);
  const [newApproverId, setNewApproverId] = useState('');
  const [rejectComments, setRejectComments] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [d, a] = await Promise.all([
      interviewsApi.getDecisions({}).catch(() => ({ data: [] })),
      interviewsApi.getMyApprovals().catch(() => ({ data: [] })),
    ]);
    setDecisions(d.data);
    setMyApprovals(a.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    hrms.getCandidates({ status: 'interview' }).then(r => setCandidates(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    interviewsApi.getEmployeesForPanel().then(r => setUsers(r.data)).catch(err => console.warn('[HRMS]', err?.message));
  }, [load]);

  const handleCreate = async () => {
    const data = {
      ...form,
      candidate_id: parseInt(form.candidate_id),
      final_score: form.final_score !== '' ? parseFloat(form.final_score) : null,
      approver_ids: approverIds.map(Number),
    };
    try {
      await interviewsApi.createDecision(data);
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating decision');
    }
  };

  const handleApprove = async (decisionId) => {
    await interviewsApi.approveDecision(decisionId).catch(e => alert(e.response?.data?.detail));
    load();
  };

  const handleReject = async (decisionId) => {
    if (!rejectComments || rejectComments.length < 10) {
      alert('Rejection reason must be at least 10 characters');
      return;
    }
    await interviewsApi.rejectDecision(decisionId, rejectComments).catch(e => alert(e.response?.data?.detail));
    setModal(null);
    load();
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      {myApprovals.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={s.sectionTitle}>My Pending Approvals</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myApprovals.map(a => (
              <Card key={a.step_id} style={{ borderLeft: `3px solid ${theme.warning}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong>{a.candidate_name}</strong>
                    <span style={{ color: theme.textMuted, fontSize: 13 }}> · Outcome: {fmt(a.outcome)} · Step {a.step_number}</span>
                    {a.recommendation_notes && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{a.recommendation_notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" onClick={() => handleApprove(a.decision_id)}>Approve</Button>
                    <Button size="sm" variant="danger" onClick={() => { setSelected(a); setModal('reject'); }}>Reject</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div style={s.actions}>
        {isHR && <Button onClick={() => { setForm(EMPTY_DECISION); setApproverIds([]); setModal('create'); }}>+ Create Decision</Button>}
      </div>

      <Table
        columns={[
          { header: 'Candidate', accessor: 'candidate_name' },
          { header: 'Outcome', render: r => <span style={s.pill(r.outcome === 'select' ? theme.success : r.outcome === 'reject' ? theme.danger : theme.warning)}>{fmt(r.outcome)}</span> },
          { header: 'Status', render: r => <span style={s.pill(r.status === 'approved' ? theme.success : r.status === 'rejected' ? theme.danger : theme.warning)}>{fmt(r.status)}</span> },
          { header: 'Score', render: r => r.final_score ?? '—' },
          { header: 'Approvals', render: r => `${r.approval_steps.filter(s => s.status === 'approved').length}/${r.approval_steps.length}` },
          { header: 'Created', render: r => fmtDateOnly(r.created_at) },
          {
            header: 'Actions', render: r => (
              <div style={{ display: 'flex', gap: 4 }}>
                <Button size="xs" variant="secondary" onClick={() => { setSelected(r); setModal('view'); }}>View</Button>
                {r.status === 'pending_approval' && (
                  <Button size="xs" onClick={() => handleApprove(r.id)}>Approve</Button>
                )}
              </div>
            )
          },
        ]}
        data={decisions}
      />

      {/* Create Decision Modal */}
      <Modal isOpen={modal === 'create'} onClose={() => setModal(null)} title="Create Hiring Decision">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Candidate *</label>
            <SearchableSelect
              options={candidates.map(c => ({ value: c.id, label: c.full_name, meta: c.position_applied }))}
              value={form.candidate_id}
              onChange={v => setForm(f => ({ ...f, candidate_id: v }))}
              placeholder="Select candidate"
            /></div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Outcome *</label>
              <select style={s.select} value={form.outcome} onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}>
                {OUTCOME_OPTS.map(o => <option key={o} value={o}>{fmt(o)}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Final Score</label>
              <input style={s.input} type="number" step={0.1} value={form.final_score}
                onChange={e => setForm(f => ({ ...f, final_score: e.target.value }))} /></div>
          </div>
          <div style={s.field}><label style={s.label}>Recommendation Notes</label>
            <textarea style={s.textarea} value={form.recommendation_notes}
              onChange={e => setForm(f => ({ ...f, recommendation_notes: e.target.value }))} /></div>
          {form.outcome === 'reject' && (
            <div style={s.field}><label style={s.label}>Rejection Reason</label>
              <textarea style={s.textarea} value={form.rejection_reason || ''}
                onChange={e => setForm(f => ({ ...f, rejection_reason: e.target.value }))} /></div>
          )}
          <hr style={s.divider} />
          <div style={s.label}>Approval Chain</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SearchableSelect
              options={users.map(u => ({ value: u.id, label: u.full_name, meta: u.role }))}
              value={newApproverId}
              onChange={v => setNewApproverId(v)}
              placeholder="Select approver"
              style={{ flex: 1 }}
            />
            <Button size="sm" onClick={() => {
              if (newApproverId) { setApproverIds(a => [...a, parseInt(newApproverId)]); setNewApproverId(''); }
            }}>+ Add</Button>
          </div>
          {approverIds.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {approverIds.map((id, i) => {
                const u = users.find(u => u.id === id);
                return (
                  <span key={i} style={{ ...s.pill(theme.info), display: 'flex', alignItems: 'center', gap: 4 }}>
                    Step {i + 1}: {u?.full_name || id}
                    <span style={{ cursor: 'pointer' }} onClick={() => setApproverIds(a => a.filter((_, j) => j !== i))}>×</span>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Decision</Button>
          </div>
        </div>
      </Modal>

      {/* View Decision */}
      <Modal isOpen={modal === 'view'} onClose={() => setModal(null)} title="Decision Detail">
        {selected && (
          <div>
            <div style={s.grid2}>
              <div><span style={s.label}>Candidate:</span> {selected.candidate_name}</div>
              <div><span style={s.label}>Outcome:</span> <span style={s.pill(selected.outcome === 'select' ? theme.success : theme.danger)}>{fmt(selected.outcome)}</span></div>
              <div><span style={s.label}>Status:</span> {fmt(selected.status)}</div>
              <div><span style={s.label}>Final Score:</span> {selected.final_score ?? '—'}</div>
            </div>
            {selected.recommendation_notes && <div style={{ marginTop: 8 }}><span style={s.label}>Notes:</span> {selected.recommendation_notes}</div>}
            {selected.rejection_reason && <div style={{ marginTop: 4, color: theme.danger }}><span style={s.label}>Rejection:</span> {selected.rejection_reason}</div>}
            <hr style={s.divider} />
            <div style={s.label}>Approval Steps</div>
            {(selected.approval_steps || []).map(step => (
              <div key={step.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <span style={s.pill(step.status === 'approved' ? theme.success : step.status === 'rejected' ? theme.danger : theme.textMuted)}>
                  Step {step.step_number}
                </span>
                <span style={{ fontSize: 13 }}>{step.approver_name}</span>
                <span style={{ fontSize: 12, color: theme.textMuted }}>{fmt(step.status)}</span>
                {step.comments && <span style={{ fontSize: 12, color: theme.textMuted }}>— {step.comments}</span>}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={modal === 'reject'} onClose={() => setModal(null)} title="Reject Decision">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Rejection Reason (min 10 chars)</label>
            <textarea style={s.textarea} value={rejectComments}
              onChange={e => setRejectComments(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => handleReject(selected?.decision_id)}>Reject</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// OFFERS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function OffersTab({ isHR }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_OFFER_NEW);
  const [candidates, setCandidates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [responseVal, setResponseVal] = useState('accepted');
  const [revokeReason, setRevokeReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await interviewsApi.getOffers({}).catch(() => ({ data: [] }));
    setOffers(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    hrms.getCandidates({}).then(r => setCandidates(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    hrms.getDepartments().then(r => setDepartments(r.data)).catch(err => console.warn('[HRMS]', err?.message));
    interviewsApi.getSalaryGrades().then(r => setGrades(r.data)).catch(err => console.warn('[HRMS]', err?.message));
  }, [load]);

  const handleCreate = async () => {
    const data = {
      candidate_id: parseInt(form.candidate_id),
      designation: form.designation,
      department_id: form.department_id ? parseInt(form.department_id) : null,
      grade_id: form.grade_id ? parseInt(form.grade_id) : null,
      offered_ctc: form.offered_ctc !== '' ? parseFloat(form.offered_ctc) : null,
      joining_date: form.joining_date || null,
      offer_valid_until: form.offer_valid_until || null,
      offer_notes: form.offer_notes || null,
    };
    try {
      await interviewsApi.createOffer(data);
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating offer');
    }
  };

  const handleAction = async (action, offerId) => {
    try {
      if (action === 'submit') await interviewsApi.submitOfferForApproval(offerId);
      else if (action === 'approve') await interviewsApi.approveOffer(offerId);
      else if (action === 'send') await interviewsApi.sendOffer(offerId);
      else if (action === 'response') { await interviewsApi.recordOfferResponse(offerId, responseVal); setModal(null); }
      else if (action === 'revoke') { await interviewsApi.revokeOffer(offerId, revokeReason); setModal(null); }
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      <div style={s.actions}>
        {isHR && <Button onClick={() => { setForm(EMPTY_OFFER_NEW); setModal('create'); }}>+ Create Offer</Button>}
      </div>

      <Table
        columns={[
          { header: 'Offer #', accessor: 'offer_number', render: val => val || '—' },
          { header: 'Candidate', accessor: 'candidate_name' },
          { header: 'Designation', accessor: 'designation' },
          { header: 'CTC (LPA)', render: r => r.offered_ctc != null ? r.offered_ctc.toFixed(2) : '—' },
          { header: 'Joining', render: r => fmtDateOnly(r.joining_date) },
          {
            header: 'Status', render: r => (
              <span style={s.pill(OFFER_STATUS_COLORS[r.status] || theme.textMuted)}>{fmt(r.status)}</span>
            )
          },
          {
            header: 'Actions', render: r => (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Button size="xs" variant="secondary" onClick={() => { setSelected(r); setModal('view'); }}>View</Button>
                {isHR && r.status === 'draft' && <Button size="xs" onClick={() => handleAction('submit', r.id)}>Submit</Button>}
                {r.status === 'pending_approval' && <Button size="xs" onClick={() => handleAction('approve', r.id)}>Approve</Button>}
                {isHR && r.status === 'approved' && <Button size="xs" onClick={() => handleAction('send', r.id)}>Send</Button>}
                {isHR && r.status === 'sent' && <Button size="xs" variant="secondary" onClick={() => { setSelected(r); setModal('response'); }}>Record Response</Button>}
                {isHR && ['draft', 'pending_approval', 'approved', 'sent'].includes(r.status) && (
                  <Button size="xs" variant="danger" onClick={() => { setSelected(r); setRevokeReason(''); setModal('revoke'); }}>Revoke</Button>
                )}
              </div>
            )
          },
        ]}
        data={offers}
      />

      {/* Create Offer */}
      <Modal isOpen={modal === 'create'} onClose={() => setModal(null)} title="Create Offer Letter">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Candidate *</label>
            <SearchableSelect
              options={candidates.map(c => ({ value: c.id, label: c.full_name, meta: c.position_applied }))}
              value={form.candidate_id}
              onChange={v => setForm(f => ({ ...f, candidate_id: v }))}
              placeholder="Select candidate"
            /></div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Designation *</label>
              <input style={s.input} value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Department</label>
              <SearchableSelect
                options={departments.map(d => ({ value: d.id, label: d.name }))}
                value={form.department_id}
                onChange={v => setForm(f => ({ ...f, department_id: v }))}
                placeholder="Select department"
              /></div>
          </div>
          <div style={s.grid3}>
            <div style={s.field}><label style={s.label}>Salary Grade</label>
              <select style={s.select} value={form.grade_id} onChange={e => setForm(f => ({ ...f, grade_id: e.target.value }))}>
                <option value="">Select</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.grade_code} — {g.grade_name} ({g.min_ctc}–{g.max_ctc} LPA)</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Offered CTC (LPA)</label>
              <input style={s.input} type="number" step={0.1} value={form.offered_ctc}
                onChange={e => setForm(f => ({ ...f, offered_ctc: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Valid Until</label>
              <input style={s.input} type="date" value={form.offer_valid_until}
                onChange={e => setForm(f => ({ ...f, offer_valid_until: e.target.value }))} /></div>
          </div>
          <div style={s.field}><label style={s.label}>Joining Date</label>
            <input style={s.input} type="date" value={form.joining_date}
              onChange={e => setForm(f => ({ ...f, joining_date: e.target.value }))} /></div>
          <div style={s.field}><label style={s.label}>Offer Notes</label>
            <textarea style={s.textarea} value={form.offer_notes}
              onChange={e => setForm(f => ({ ...f, offer_notes: e.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Offer</Button>
          </div>
        </div>
      </Modal>

      {/* View Offer */}
      <Modal isOpen={modal === 'view'} onClose={() => setModal(null)} title={`Offer — ${selected?.offer_number || ''}`}>
        {selected && (
          <div>
            <div style={s.grid2}>
              <div><span style={s.label}>Candidate:</span> {selected.candidate_name}</div>
              <div><span style={s.label}>Designation:</span> {selected.designation}</div>
              <div><span style={s.label}>Department:</span> {selected.department_name || '—'}</div>
              <div><span style={s.label}>CTC:</span> {selected.offered_ctc != null ? `${selected.offered_ctc} LPA` : '—'}</div>
              <div><span style={s.label}>Joining Date:</span> {fmtDateOnly(selected.joining_date)}</div>
              <div><span style={s.label}>Valid Until:</span> {fmtDateOnly(selected.offer_valid_until)}</div>
              <div><span style={s.label}>Sent At:</span> {selected.sent_at ? fmtDate(selected.sent_at) : '—'}</div>
              <div><span style={s.label}>Response:</span> {selected.candidate_response ? fmt(selected.candidate_response) : '—'}</div>
            </div>
            {selected.offer_notes && <div style={{ marginTop: 8 }}><span style={s.label}>Notes:</span> {selected.offer_notes}</div>}
            <div style={{ marginTop: 8 }}>
              <span style={s.pill(OFFER_STATUS_COLORS[selected.status] || theme.textMuted)}>{fmt(selected.status)}</span>
            </div>
          </div>
        )}
      </Modal>

      {/* Record Response */}
      <Modal isOpen={modal === 'response'} onClose={() => setModal(null)} title="Record Candidate Response">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Response</label>
            <select style={s.select} value={responseVal} onChange={e => setResponseVal(e.target.value)}>
              <option value="accepted">Accepted</option>
              <option value="declined">Declined</option>
            </select></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => handleAction('response', selected?.id)}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Revoke Offer */}
      <Modal isOpen={modal === 'revoke'} onClose={() => setModal(null)} title="Revoke Offer">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Reason *</label>
            <textarea style={s.textarea} value={revokeReason}
              onChange={e => setRevokeReason(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => handleAction('revoke', selected?.id)}>Revoke</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATES TAB
// ═══════════════════════════════════════════════════════════════════════════════
function TemplatesTab({ isHR }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [newCriterion, setNewCriterion] = useState({ name: '', description: '', weight: 1.0 });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await interviewsApi.getTemplates({}).catch(() => ({ data: [] }));
    setTemplates(res.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const data = {
      name: form.name,
      description: form.description,
      interview_type: form.interview_type || null,
      criteria: (form.criteria || []).map((c, i) => ({ ...c, sort_order: i })),
    };
    try {
      if (selected) {
        await interviewsApi.updateTemplate(selected.id, { name: data.name, description: data.description, interview_type: data.interview_type });
      } else {
        await interviewsApi.createTemplate(data);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleAddCriterion = () => {
    if (!newCriterion.name) return;
    setForm(f => ({ ...f, criteria: [...(f.criteria || []), { ...newCriterion }] }));
    setNewCriterion({ name: '', description: '', weight: 1.0 });
  };

  const handleDeactivate = async (id) => {
    await interviewsApi.deleteTemplate(id);
    load();
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      <div style={s.actions}>
        {isHR && <Button onClick={() => { setForm(EMPTY_TEMPLATE); setSelected(null); setModal('form'); }}>+ Create Template</Button>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {templates.length === 0 && <div style={{ color: theme.textMuted }}>No templates created.</div>}
        {templates.map(t => (
          <Card key={t.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <strong>{t.name}</strong>
                {t.interview_type && <span style={{ ...s.pill(theme.info), marginLeft: 8 }}>{fmt(t.interview_type)}</span>}
                {t.description && <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 2 }}>{t.description}</div>}
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{(t.criteria || []).length} criteria</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="xs" variant="secondary" onClick={() => { setSelected(t); setForm({ ...t, criteria: t.criteria || [] }); setModal('form'); }}>Edit</Button>
                {isHR && t.is_active && <Button size="xs" variant="danger" onClick={() => handleDeactivate(t.id)}>Deactivate</Button>}
              </div>
            </div>
            {(t.criteria || []).length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {t.criteria.map(c => (
                  <span key={c.id} style={s.pill(theme.textMuted)}>{c.name} (×{c.weight})</span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Modal isOpen={modal === 'form'} onClose={() => setModal(null)} title={selected ? 'Edit Template' : 'Create Template'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Template Name *</label>
            <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Interview Type</label>
              <select style={s.select} value={form.interview_type} onChange={e => setForm(f => ({ ...f, interview_type: e.target.value }))}>
                <option value="">All Types</option>
                {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Description</label>
              <input style={s.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          </div>

          {!selected && (
            <>
              <hr style={s.divider} />
              <div style={s.label}>Evaluation Criteria</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={s.field}>
                  <label style={s.label}>Criterion Name</label>
                  <input style={{ ...s.input, width: 180 }} value={newCriterion.name}
                    onChange={e => setNewCriterion(c => ({ ...c, name: e.target.value }))} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Weight</label>
                  <input style={{ ...s.input, width: 80 }} type="number" step={0.5} min={0.5} value={newCriterion.weight}
                    onChange={e => setNewCriterion(c => ({ ...c, weight: parseFloat(e.target.value) }))} />
                </div>
                <Button size="sm" onClick={handleAddCriterion}>+ Add</Button>
              </div>
              {(form.criteria || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', background: theme.bg, borderRadius: 6, padding: '6px 10px' }}>
                  <span style={{ flex: 1, fontSize: 13 }}>{c.name}</span>
                  <span style={{ color: theme.textMuted, fontSize: 12 }}>weight: {c.weight}</span>
                  <span style={{ cursor: 'pointer', color: theme.danger }} onClick={() => setForm(f => ({ ...f, criteria: f.criteria.filter((_, j) => j !== i) }))}>×</span>
                </div>
              ))}
            </>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUESTION BANK TAB
// ═══════════════════════════════════════════════════════════════════════════════
function QuestionsTab({ isHR }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_QUESTION);
  const [filterType, setFilterType] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterType) params.interview_type = filterType;
    if (filterDiff) params.difficulty = filterDiff;
    if (search) params.search = search;
    const res = await interviewsApi.getQuestions(params).catch(() => ({ data: [] }));
    setQuestions(res.data);
    setLoading(false);
  }, [filterType, filterDiff, search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (selected) {
        await interviewsApi.updateQuestion(selected.id, form);
      } else {
        await interviewsApi.createQuestion(form);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleDeactivate = async (id) => {
    await interviewsApi.deleteQuestion(id);
    load();
  };

  const DIFF_COLORS = { easy: theme.success, medium: theme.warning, hard: theme.danger };

  return (
    <div>
      <div style={s.actions}>
        <select style={{ ...s.select, width: 150 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
        </select>
        <select style={{ ...s.select, width: 120 }} value={filterDiff} onChange={e => setFilterDiff(e.target.value)}>
          <option value="">All Levels</option>
          {DIFFICULTY_OPTS.map(d => <option key={d} value={d}>{fmt(d)}</option>)}
        </select>
        <input placeholder="Search questions…" style={{ ...s.input, width: 240 }} value={search}
          onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} />
        <Button variant="secondary" size="sm" onClick={load}>Filter</Button>
        {isHR && <Button onClick={() => { setForm(EMPTY_QUESTION); setSelected(null); setModal('form'); }}>+ Add Question</Button>}
      </div>

      {loading ? <div style={{ color: theme.textMuted }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {questions.length === 0 && <div style={{ color: theme.textMuted }}>No questions found.</div>}
          {questions.map(q => (
            <div key={q.id} style={{
              background: theme.cardBg, border: `1px solid ${theme.border}`,
              borderRadius: 8, padding: '10px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14 }}>{q.question_text}</div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6 }}>
                  {q.interview_type && <span style={s.pill(theme.info)}>{fmt(q.interview_type)}</span>}
                  <span style={s.pill(DIFF_COLORS[q.difficulty] || theme.textMuted)}>{fmt(q.difficulty)}</span>
                  {q.tags && q.tags.split(',').map(tag => (
                    <span key={tag} style={s.pill(theme.textMuted)}>{tag.trim()}</span>
                  ))}
                </div>
              </div>
              {isHR && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button size="xs" onClick={() => { setSelected(q); setForm({ ...q }); setModal('form'); }}>Edit</Button>
                  <Button size="xs" variant="danger" onClick={() => handleDeactivate(q.id)}>Remove</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal === 'form'} onClose={() => setModal(null)} title={selected ? 'Edit Question' : 'Add Question'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.field}><label style={s.label}>Question Text *</label>
            <textarea style={s.textarea} value={form.question_text}
              onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))} /></div>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Interview Type</label>
              <select style={s.select} value={form.interview_type} onChange={e => setForm(f => ({ ...f, interview_type: e.target.value }))}>
                <option value="">Any</option>
                {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{fmt(t)}</option>)}
              </select></div>
            <div style={s.field}><label style={s.label}>Difficulty</label>
              <select style={s.select} value={form.difficulty} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}>
                {DIFFICULTY_OPTS.map(d => <option key={d} value={d}>{fmt(d)}</option>)}
              </select></div>
          </div>
          <div style={s.field}><label style={s.label}>Tags (comma-separated)</label>
            <input style={s.input} value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SALARY GRADES TAB
// ═══════════════════════════════════════════════════════════════════════════════
function SalaryGradesTab({ isSuperAdmin }) {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_GRADE);
  const [departments, setDepartments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await interviewsApi.getSalaryGrades().catch(() => ({ data: [] }));
    setGrades(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    hrms.getDepartments().then(r => setDepartments(r.data)).catch(err => console.warn('[HRMS]', err?.message));
  }, [load]);

  const handleSave = async () => {
    const data = {
      grade_code: form.grade_code,
      grade_name: form.grade_name,
      department_id: form.department_id ? parseInt(form.department_id) : null,
      min_ctc: parseFloat(form.min_ctc),
      max_ctc: parseFloat(form.max_ctc),
      override_threshold_pct: parseFloat(form.override_threshold_pct),
    };
    try {
      if (selected) {
        await interviewsApi.updateSalaryGrade(selected.id, data);
      } else {
        await interviewsApi.createSalaryGrade(data);
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving grade');
    }
  };

  if (loading) return <div style={{ color: theme.textMuted }}>Loading…</div>;

  return (
    <div>
      <div style={s.actions}>
        {isSuperAdmin && <Button onClick={() => { setForm(EMPTY_GRADE); setSelected(null); setModal('form'); }}>+ Add Grade</Button>}
      </div>

      <Table
        columns={[
          { header: 'Code', accessor: 'grade_code' },
          { header: 'Name', accessor: 'grade_name' },
          { header: 'Department', render: r => r.department_name || 'All' },
          { header: 'Min CTC (LPA)', render: r => r.min_ctc.toFixed(2) },
          { header: 'Max CTC (LPA)', render: r => r.max_ctc.toFixed(2) },
          { header: 'Override %', render: r => `${r.override_threshold_pct}%` },
          {
            header: 'Actions', render: r => isSuperAdmin ? (
              <Button size="xs" onClick={() => { setSelected(r); setForm({ ...r, department_id: r.department_id || '' }); setModal('form'); }}>Edit</Button>
            ) : null
          },
        ]}
        data={grades}
      />

      <Modal isOpen={modal === 'form'} onClose={() => setModal(null)} title={selected ? 'Edit Grade' : 'Add Salary Grade'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={s.grid2}>
            <div style={s.field}><label style={s.label}>Grade Code *</label>
              <input style={s.input} placeholder="e.g. L3" value={form.grade_code}
                onChange={e => setForm(f => ({ ...f, grade_code: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Grade Name *</label>
              <input style={s.input} placeholder="e.g. Senior Engineer" value={form.grade_name}
                onChange={e => setForm(f => ({ ...f, grade_name: e.target.value }))} /></div>
          </div>
          <div style={s.field}><label style={s.label}>Department (optional)</label>
            <select style={s.select} value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>
          <div style={s.grid3}>
            <div style={s.field}><label style={s.label}>Min CTC (LPA)</label>
              <input style={s.input} type="number" step={0.5} value={form.min_ctc}
                onChange={e => setForm(f => ({ ...f, min_ctc: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Max CTC (LPA)</label>
              <input style={s.input} type="number" step={0.5} value={form.max_ctc}
                onChange={e => setForm(f => ({ ...f, max_ctc: e.target.value }))} /></div>
            <div style={s.field}><label style={s.label}>Override Threshold %</label>
              <input style={s.input} type="number" step={0.5} value={form.override_threshold_pct}
                onChange={e => setForm(f => ({ ...f, override_threshold_pct: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave}>Save Grade</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
